import { jwtDecode } from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from '../../utils/axiosConfig'; // Use the configured axios instance

const CompleteRegistration = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth, setTokens } = useAuth();

  // Get registration token from URL parameter
  const registrationToken = searchParams.get('token');

  const [currentStage, setCurrentStage] = useState(1); // 1 for name info, 2 for email/password
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    email: '', // Will be populated when we get token info
    password: '',
    confirmPassword: '',
    rememberMe: false, // Add remember me option
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [registrationType, setRegistrationType] = useState(null); // 'trial' or 'university'
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check if we have the necessary authentication and fetch email
  useEffect(() => {
    if (!registrationToken) {
      setTokenError('Registration token missing. Please return to the login page and try again.');
      return;
    }

    // Fetch registration info to get the email
    const fetchRegistrationInfo = async () => {
      try {
        const response = await axios.post('/api/v1/auth/register/info', {
          registration_token: registrationToken,
        });

        if (response.data?.email) {
          setFormData((prev) => ({
            ...prev,
            email: response.data.email,
          }));

          // Store registration type for display purposes
          if (response.data?.registration_type) {
            setRegistrationType(response.data.registration_type);
          }
        }
      } catch (err) {
        console.error('Error fetching registration info:', err);
        const errorMessage =
          err.response?.data?.detail || 'Unable to load registration information';

        if (errorMessage.includes('expired') || errorMessage.includes('Invalid')) {
          setTokenError(
            'This registration link is invalid or has expired. Please restart the registration process.'
          );
        } else {
          setTokenError('Unable to load registration information. Please try again.');
        }
      }
    };

    fetchRegistrationInfo();
  }, [registrationToken]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateStage1 = () => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    return true;
  };

  const validateStage2 = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    return true;
  };

  const handleStage1Submit = (e) => {
    e.preventDefault();
    setError('');

    if (!validateStage1()) return;

    setCurrentStage(2);
  };

  const handleStage2Submit = async (e) => {
    e.preventDefault();
    if (!validateStage2()) return;

    // Prevent duplicate submissions
    if (loading) return;

    setError('');
    setTokenError('');
    setLoading(true);

    try {
      // Prepare request data
      const requestData = {
        registration_token: registrationToken,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        middle_name: formData.middleName,
        remember_me: formData.rememberMe,
      };

      const response = await axios.post('/api/v1/auth/register/complete', requestData);

      // Registration successful, user is automatically logged in
      console.log('Registration successful, setting tokens...');

      // Use AuthContext to set tokens (this will also set user state and isAuthenticated)
      if (response.data.access_token) {
        setTokens(response.data.access_token);
        console.log('Tokens set via AuthContext');
      }

      // Store user ID for compatibility (some components might still use it)
      const userId = response.data.user?.id || response.data.user?.user_id;
      if (userId) {
        localStorage.setItem('userID', userId);
        console.log('User ID stored:', userId);
      } else {
        // Fallback: Try to get user ID from access token
        try {
          const decodedToken = jwtDecode(response.data.access_token);
          if (decodedToken.sub) {
            localStorage.setItem('userID', decodedToken.sub);
            console.log('User ID from token stored:', decodedToken.sub);
          }
        } catch (jwtError) {
          console.error('Failed to decode access token:', jwtError);
        }
      }

      console.log('Registration complete, redirecting to home...');
      // Redirect to home page (not progress-tree)
      navigate('/home');
    } catch (err) {
      setLoading(false);

      // Check for specific error messages
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Registration failed. Please try again or request a new verification link.';
      console.error('Registration error:', errorMessage);

      // Specifically check for token expiration messages
      if (
        errorMessage.includes('expired') ||
        errorMessage.includes('Registration token has expired')
      ) {
        setTokenError(
          'Your registration token has expired. Please restart the registration process.'
        );
      } else if (
        errorMessage.includes('Invalid') ||
        errorMessage.includes('invalid') ||
        errorMessage.includes('Invalid or expired registration token')
      ) {
        setTokenError(
          'This registration link is invalid. Please restart the registration process.'
        );
      } else {
        setError(errorMessage);
      }
    }
  };

  const renderStage1 = () => {
    return (
      <>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img
              src={process.env.PUBLIC_URL + '/maincar.png'}
              alt="InternHub Logo"
              className="h-14 mb-2"
            />
            <span className="text-blue-600 text-3xl font-bold ml-2 self-center">InternHub</span>
          </div>
          <h2 className="mt-4 text-center text-3xl font-extrabold text-gray-900">
            Tell us about yourself
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xl">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleStage1Submit}>
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <div className="mt-1">
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <div className="mt-1">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="middleName" className="block text-sm font-medium text-gray-700">
                  Middle Name (Optional)
                </label>
                <div className="mt-1">
                  <input
                    id="middleName"
                    name="middleName"
                    type="text"
                    autoComplete="additional-name"
                    value={formData.middleName}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Continue
                </button>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  };

  const renderStage2 = () => {
    return (
      <>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img
              src={process.env.PUBLIC_URL + '/maincar.png'}
              alt="InternHub Logo"
              className="h-14 mb-2"
            />
            <span className="text-blue-600 text-3xl font-bold ml-2 self-center">InternHub</span>
          </div>
          <h2 className="mt-4 text-center text-3xl font-extrabold text-gray-900">
            Set up your password
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xl">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleStage2Submit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    readOnly
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs bg-gray-50 text-gray-600 sm:text-sm"
                    placeholder="Loading email..."
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Your email address from the verification link
                </p>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Create a strong password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded-sm"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-900">
                  Keep me signed in for 30 days
                </label>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? 'Creating account...' : 'Complete Registration'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setCurrentStage(1);
                    setError('');
                  }}
                  className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-xs bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Back to personal information
                </button>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  };

  if (!registrationToken || tokenError) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img
              src={process.env.PUBLIC_URL + '/maincar.png'}
              alt="InternHub Logo"
              className="h-14 mb-2"
            />
            <span className="text-blue-600 text-3xl font-bold ml-2 self-center">InternHub</span>
          </div>
          <h2 className="mt-4 text-center text-3xl font-extrabold text-gray-900">
            {tokenError ? 'Registration Error' : 'Invalid Registration Link'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {tokenError || 'This registration link is invalid or has expired.'}
          </p>
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-xs text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Return to registration
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {currentStage === 1 ? renderStage1() : renderStage2()}
    </div>
  );
};

export default CompleteRegistration;
